import React, {useEffect, useRef, useState} from 'react';
import {Dialog, DialogContent, Typography, Backdrop, Box} from '@mui/material';
import axios from 'axios';
import {ReactComponent as CloseIcon} from '../../assets/images/prizmdoc-icons/close.svg';

import './DocPreviewModal.scss';
import ArrIconButton from '../common/arr-icon-button/ArrIconButton';
import {DocToolbar} from './DocToolbar';
import {DocumentPathType, usePrizmDocAnnotations} from './hooks/usePrizmDoc';
import {DocForPreview} from '../doc-explorer/doc-explorer-data-grid/DocExplorerDataGrid';
import {useGetFilePath} from '../../hooks/document/useDocument';
import {DocumentArea} from './DocumentArea';
import {t} from 'i18next';
import {FullscreenExitOutlined, FullscreenOutlined} from '@mui/icons-material';
import {ReactComponent as FolderIcon} from '../../assets/images/no-data-found.svg';

const MODAL_TRANSITION = 300;
const ACCESS_DENIED_ERROR = 403;

export interface PreviewDocumentProps {
  showModal: boolean;
  onClose: () => void;
  document: DocForPreview;
}

const DocPreviewModal: React.FC<PreviewDocumentProps> = ({
  showModal,
  onClose,
  document,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const [fullScreen, setFullscreen] = useState(false);
  const {data: docData, error: docPathError} = useGetFilePath(
    {
      documentId: document?.id,
      documentName: document?.docName,
      fileType: 'viewer',
    },
    true
  );
  const containerRef = useRef<HTMLElement>(null);

  const [docFilePath, setDocFilePath] = useState<DocumentPathType | null>(null);
  const {
    viewerControl,
    thumbnailControl,
    totalPages,
    pageNumber,
    initialScaleFactor,
    isViewerReady,
  } = usePrizmDocAnnotations(docFilePath, viewerRef);

  const [_remark, setRemark] = useState<Mark | null>(null);

  const hasAccessDenied =
    axios.isAxiosError(docPathError) &&
    docPathError.response?.status === ACCESS_DENIED_ERROR;

  useEffect(() => {
    if (docData) {
      setDocFilePath({
        documentId: docData.docID,
        documentExt: docData.documentExtension,
        documentPath: docData.documentPath,
      });
    }
  }, [docData, setDocFilePath]);
  return (
    <Dialog
      open={showModal}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      className={`doc-preview-modal`}
      keepMounted
      data-testid={'doc-preview-modal-content'}
      aria-label={'Document Viewer Preview'}
      slots={{backdrop: Backdrop}}
      slotProps={{
        transition: {timeout: MODAL_TRANSITION},
        backdrop: {className: 'modal-backdrop'},
        paper: {
          className: `modal-paper ${fullScreen ? 'full-screen' : ''} ${
            hasAccessDenied ? 'access-denied' : ''
          }`,
          'data-testid': 'preview-modal-paper-root',
        } as React.HTMLAttributes<HTMLDivElement>,
      }}
    >
      {/* Header */}
      <Box className="modal-header-title">
        <Box className="header-container">
          <Box className="title-container">
            <Typography
              className={`typo-text headline3_size doc-preview-title`}
              data-testid={`modal-header-title`}
            >
              {t('docExplorer.preview.title')}
            </Typography>
            <Box className="button-container">
              <ArrIconButton
                ariaLabel={`${fullScreen ? t('common.collapse') : t('common.expand')} button`}
                size="large"
                onClick={() => {
                  setFullscreen((prev) => !prev);
                  viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);
                }}
                icon={
                  fullScreen ? (
                    <FullscreenExitOutlined fontSize="small" />
                  ) : (
                    <FullscreenOutlined fontSize="small" />
                  )
                }
                tooltipContent={
                  fullScreen ? t('common.collapse') : t('common.expand')
                }
                data-testid="fullscreen-toggle-button"
                tooltipKey={fullScreen ? 'expanded' : 'collapsed'}
                testId="doc-preview-modal-icon-button"
              ></ArrIconButton>
              <ArrIconButton
                ariaLabel="close"
                size="large"
                onClick={onClose}
                icon={<CloseIcon fontSize="small" />}
                tooltipContent="Close"
                testId="doc-preview-modal-close-icon-button"
              ></ArrIconButton>
            </Box>
          </Box>
          <Box
            className={`toolbar-wrapper ${hasAccessDenied ? 'access-denied' : ''}`}
          >
            <Typography
              data-testid={`doc-preview-modal-docId`}
              className={`subhead4_size controls-text ${hasAccessDenied ? 'access-denied-header' : ''}`}
            >
              {`DocID: ${document.docId}`}
            </Typography>
            {!hasAccessDenied && viewerControl && (
              <DocToolbar
                viewerControl={viewerControl}
                pageNumber={pageNumber}
                totalPages={totalPages}
                initialScaleFactor={initialScaleFactor}
                isReady={isViewerReady}
                documentDetails={document}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent
        className={`modal-content ${hasAccessDenied ? 'access-denied' : ''}`}
        ref={containerRef}
      >
        {hasAccessDenied ? (
          <AccessDeniedOverlay />
        ) : (
          <DocumentArea
            thumbRef={thumbRef}
            rightPanelRef={rightPanelRef}
            viewerRef={viewerRef}
            containerRef={containerRef}
            viewerControl={viewerControl}
            thumbnailControl={thumbnailControl}
            documentId={document?.id}
            markForRemark={null}
            setRemark={setRemark}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DocPreviewModal;

const AccessDeniedOverlay: React.FC = () => {
  return (
    <Box
      className="access-denied-message"
      role="alert"
      aria-live="polite"
      data-testid="access-denied-overlay"
    >
      <FolderIcon
        className="access-denied-icon"
        data-testid="access-denied-icon"
      />
      <Typography
        className="typo-text headline3_size access-denied-text"
        data-testid="access-denied-text"
      >
        {t('common.accessDeniedDocument')}
      </Typography>
    </Box>
  );
};
